/**
 * Navy collapsible sidebar (E10-F01-T03).
 *
 * Parity with the legacy `core.js` chrome:
 *   - bg-slate-900, 256px expanded → 60px collapsed (collapse persisted in
 *     localStorage by the parent `AppShell`);
 *   - brand wordmark 'EER GLOBAL' in gold (`text-[#D4AF37]` + brand-font);
 *   - nav-items `p-3 rounded hover:bg-slate-800`; active item gets
 *     `bg-slate-800 text-[#D4AF37]`, every other item is reset on navigation;
 *   - icon `w-6` + label `.sidebar-label` (hidden in collapsed; nav-item then
 *     centers — handled by the `.sidebar-collapsed` CSS rules);
 *   - bottom block: round slate-700 avatar with gold initials + name + UPPERCASE
 *     role (legacy `.toUpperCase()`), then the Outlook connect/disconnect widget
 *     (legacy `#sidebar-outlook-btn`), rendered via the shared `OutlookConnect`
 *     component in its stacked `sidebar` variant, and finally the "Log out"
 *     button pinned at the very bottom (legacy `app.logout()`; FP-15 reverted the
 *     brief move of this control into the top header).
 *
 * Colours are written as literal brand hexes (`#D4AF37`, slate-*) to match the
 * legacy markup the QA Gate diffs against; these hexes are the design.md `accent`
 * / `primary` family. The whole shell lives under `.sidebar-collapsed` toggled
 * by the parent, which is what drives the label hide + centering.
 */
import type { AuthIdentity } from '@eer/shared';
import type { Route, ViewId } from '../router/routes.js';
import { visibleNavItems } from './nav-items.js';
import { OutlookConnect } from '../admin/OutlookConnect.js';

export interface SidebarProps {
  readonly collapsed: boolean;
  readonly route: Route;
  readonly user: AuthIdentity | null;
  readonly onToggle: () => void;
  readonly onNavigate: (view: ViewId) => void;
  /**
   * Sign out — best-effort server logout + local clear (flips App back to the
   * login screen). Rendered as the "Log out" button at the BOTTOM of the sidebar
   * footer (legacy `core.js` chrome — index.html `app.logout()`); the PM reverted
   * the earlier move of this control up into the header (FP-15). Omitted → no
   * logout button (e.g. an unauthenticated shell).
   */
  readonly onLogout?: () => void;
  /**
   * Unread counts keyed by view (currently only `inbox`). A positive count
   * renders the badge on its nav-item; `0`/absent hides it. In the collapsed
   * sidebar the badge floats top/right over the icon — the label that normally
   * carries it inline is hidden (`.sidebar-collapsed .nav-badge`). Supplied by
   * the Inbox feature; omitted here means "no badges yet".
   */
  readonly badges?: Partial<Record<ViewId, number>>;
}

/** Cap large unread counts so the badge stays icon-sized (e.g. `99+`). */
function badgeText(count: number): string {
  return count > 99 ? '99+' : String(count);
}

/**
 * Avatar label (gold). Legacy `updateUserInfo` wrote `u.avatar` verbatim into
 * `#current-user-avatar` (a short label/emoji column), so prefer that field when
 * present; otherwise fall back to two-letter uppercase initials from the name.
 */
function avatarLabel(user: AuthIdentity | null): string {
  const avatar = user?.avatar?.trim();
  if (avatar) {
    return avatar;
  }
  const name = user?.name;
  if (!name) {
    return '··';
  }
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0] ?? '');
  return (letters.join('') || name.slice(0, 2)).toUpperCase();
}

export function Sidebar({
  collapsed,
  route,
  user,
  onToggle,
  onNavigate,
  onLogout,
  badges,
}: SidebarProps): React.JSX.Element {
  const items = visibleNavItems(user?.role);

  return (
    <aside
      id="sidebar"
      data-testid="sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
      className={`sidebar-shell flex h-screen flex-col bg-slate-900 text-slate-200 transition-[width] duration-200 ${
        collapsed ? 'sidebar-collapsed w-[60px]' : 'w-64'
      }`}
    >
      {/* Brand + collapse toggle */}
      <div className="flex items-center justify-between gap-2 px-4 py-5">
        <span className="brand-font sidebar-label whitespace-nowrap text-lg font-bold tracking-wide text-[#D4AF37]">
          EER GLOBAL
        </span>
        <button
          type="button"
          id="sidebar-toggle"
          data-testid="sidebar-toggle"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-300 hover:bg-slate-800 hover:text-[#D4AF37]"
        >
          <i
            className={`fa-solid ${collapsed ? 'fa-angles-right' : 'fa-angles-left'}`}
            aria-hidden
          />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col gap-1 px-2" aria-label="Primary">
        {items.map((item) => {
          const active = route.view === item.view;
          const count = item.badge ? (badges?.[item.view] ?? 0) : 0;
          const showBadge = count > 0;
          return (
            <button
              key={item.id}
              type="button"
              id={`nav-${item.id}`}
              data-testid={`nav-${item.id}`}
              data-active={active ? 'true' : 'false'}
              aria-current={active ? 'page' : undefined}
              onClick={() => onNavigate(item.view)}
              className={`nav-item relative flex items-center gap-3 rounded p-3 text-left text-sm font-medium hover:bg-slate-800 ${
                active ? 'bg-slate-800 text-[#D4AF37]' : 'text-slate-200'
              }`}
            >
              <i className={`fa-solid ${item.icon} w-6 text-center`} aria-hidden />
              <span className="sidebar-label whitespace-nowrap">{item.label}</span>
              {showBadge ? (
                <span
                  className="nav-badge ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#D4AF37] px-1.5 text-xs font-semibold leading-none text-slate-900"
                  data-testid={`nav-${item.id}-badge`}
                  aria-label={`${count} unread`}
                >
                  {badgeText(count)}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      {/* User block + Outlook connect widget (legacy sidebar footer) */}
      <div className="mt-auto border-t border-slate-800 p-3">
        <div className="mb-3 flex items-center gap-3" data-testid="sidebar-user">
          <span className="brand-font flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-700 text-sm font-bold text-[#D4AF37]">
            {avatarLabel(user)}
          </span>
          <span className="sidebar-label flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium text-slate-100">
              {user?.name ?? 'Not signed in'}
            </span>
            {/* Legacy `.toUpperCase()` — role is rendered UPPERCASE (was capitalize). */}
            <span className="truncate text-xs uppercase text-slate-400">
              {user ? (user.role || '—').toUpperCase() : '—'}
            </span>
          </span>
        </div>

        {/* Connect Outlook / connected-email chip with a Disconnect action for
            the CURRENT user (legacy `#sidebar-outlook-btn` / loadOutlookStatus).
            Reuses the E08 `OutlookConnect` component in its stacked `sidebar`
            variant; status comes from the session `useOutlookStatus` query
            (userId omitted = self). Hidden in the collapsed sidebar via the
            `.sidebar-label` wrapper — same as the legacy label rules. */}
        {user ? (
          <div className="sidebar-label" data-testid="sidebar-outlook-btn">
            <OutlookConnect variant="sidebar" testId="sidebar-outlook" />
          </div>
        ) : null}

        {/* "Help" — the in-app user guide (E13-F01). Deliberately NOT a
            `NAV_ITEMS` entry: the approved spec places it in this footer block
            next to Connect Outlook / Log out, because it is an always-available
            reference rather than a workspace. Technically it is still ordinary
            navigation to `view: 'help'` through the same `useRouter`. Visible to
            every role. The active state mirrors the nav-item treatment (gold on
            slate-800) so "you are here" reads the same everywhere in the
            sidebar; the icon survives the collapsed state while
            `.sidebar-label` hides the text. */}
        <button
          type="button"
          id="nav-help"
          data-testid="help-nav-item"
          data-active={route.view === 'help' ? 'true' : 'false'}
          aria-current={route.view === 'help' ? 'page' : undefined}
          onClick={() => onNavigate('help')}
          aria-label="Help"
          className={`mt-3 flex w-full items-center justify-center gap-2 rounded border py-2 text-sm transition ${
            route.view === 'help'
              ? 'border-slate-700 bg-slate-800 text-[#D4AF37]'
              : 'border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white'
          }`}
        >
          <i className="fa-solid fa-circle-question" aria-hidden />
          <span className="sidebar-label">Help</span>
        </button>

        {/* "Log out" at the BOTTOM of the sidebar (legacy `core.js` chrome,
            index.html `app.logout()` — full-width slate-700-bordered button, the
            icon stays visible when collapsed while `.sidebar-label` hides the
            text). PM reverted the earlier move of this control into the header
            (FP-15). Keeps `id`/`data-testid="logout"` — the e2e suite waits on it
            as the "shell authenticated" signal (global-setup.ts). */}
        {onLogout ? (
          <button
            type="button"
            id="logout"
            data-testid="logout"
            onClick={onLogout}
            aria-label="Log out"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded border border-slate-700 py-2 text-sm text-slate-400 transition hover:bg-slate-800 hover:text-white"
          >
            <i className="fa-solid fa-right-from-bracket" aria-hidden />
            <span className="sidebar-label">Log out</span>
          </button>
        ) : null}
      </div>
    </aside>
  );
}
